// Formatlash yordamchilarining PANEL DARVOZASI (barrel).
//
// ⚠️ SAHIFALAR SHU FAYLDAN IMPORT QILADI (`@/shared/lib/format`) — pastdagi
// `export *` qatorlari tufayli nom ro'yxati o'zgarmadi.
//
// Bu faylda FAQAT shu panelga xos qism qoladi — VAQT ZONASI va KUN
// CHEGARASI. Bayt/son/pul qismi uchala panelda bir xil bo'lgani uchun
// `frontend/shared/format/` ga chiqarilgan va shu papkaga
// KO'CHIRILADI (#81, `../shared/sync.mjs`).
//
// ⚠️ ZONA QISMI NEGA KO'CHIRILMADI: admin va selfcare dagi imzolari bir
// xil emas (`dateTimeFormat` argument tartibi, `utcTooltip` ajratgichi,
// `zoneOffsetLabel` ning zaxira qiymati), portalda esa u UMUMAN ishlatilmaydi.
// Ularni birlashtirish — xulqni o'zgartirish, ya'ni alohida ish. Ikkala
// panelning zona qismi o'z sinovlari bilan qoplangan
// (`tests/timezone.mjs`), ya'ni bu ajralish JIM emas.
//
// Backend: vaqtlar — ISO-8601 UTC. UI: TENANT vaqt zonasi (brauzernikini
// emas), tooltip'da UTC ham.

import i18n, { currentIntlLocale } from '@/app/i18n'

export * from './format.units'
export * from './format.money'

/**
 * KO'RSATISH UCHUN VAQT ZONASI — tenantniki, brauzerniki EMAS.
 *
 * NEGA BRAUZER ZONASI YARAMAYDI: bitta tenantda ishlaydigan ikki xodim turli
 * zonalarda bo'lishi mumkin (Toshkent UTC+5, Kobul UTC+4:30) va bir xil
 * sessiyani ikki xil soatda ko'rardi. Bundan ham yomoni — KUN CHEGARASI
 * backendda tenant zonasida hisoblanadi (`dayTimezone`), ya'ni hisobotdagi
 * "bugun" va ro'yxatdagi "bugun" bir kun bo'lmasdi: "hisobotda 3 GB,
 * ro'yxatda 2.4 GB" degan chalkashlik aynan shundan chiqadi.
 *
 * ⚠️ MODUL DARAJASIDAGI QIYMAT REAKTIV EMAS — `currencyCode` bilan bir xil
 * tartib: u ILDIZDA (`AuthProvider`), `me` javobidan, bolalar chizilishidan
 * OLDIN o'rnatiladi. Sababi va oqibati o'sha izohda batafsil yozilgan.
 *
 * `null` — hali o'rnatilmagan degani, u holda brauzer zonasi ishlatiladi:
 * vaqt hech qachon ko'rsatilmay qolmaydi.
 */
let displayTimeZone: string | null = null

/** Brauzer aytgan IANA zonasi. Ba'zi eski muhitlarda bo'sh bo'lishi mumkin. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/**
 * Zona nomi haqiqiymi — `Intl` noma'lum zonada ISTISNO tashlaydi.
 * Tekshiruv shuning uchun kerak: bitta noto'g'ri qiymat panelning HAMMA
 * vaqt maydonini bo'shatib qo'yardi.
 */
export function isValidTimeZone(zone: string): boolean {
  const value = zone.trim()
  if (!value) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value })
    return true
  } catch {
    return false
  }
}

/**
 * Tenant zonasini o'rnatadi. Noto'g'ri qiymat RAD ETILADI va oldingisi
 * qoladi — server yoki operator xatosi butun panelni vaqtsiz qoldirmasin.
 */
export function setDisplayTimeZone(zone: string | null | undefined): void {
  if (zone && isValidTimeZone(zone)) displayTimeZone = zone.trim()
}

/** Amaldagi ko'rsatish zonasi: tenantniki bo'lsa o'sha, aks holda brauzerniki */
export function getDisplayTimeZone(): string {
  return displayTimeZone ?? browserTimeZone()
}

/**
 * Ixtiyoriy IANA zonasining siljish belgisi: "UTC+04:30".
 *
 * NEGA `getTimezoneOffset` EMAS: u FAQAT brauzer zonasini biladi. Bu yerda
 * esa boshqa zonaning (masalan Kobulning) siljishi kerak — uni `Intl` dan
 * olishning yagona yo'li `longOffset`. U "GMT+04:30" beradi, biz esa panel
 * bo'ylab "UTC" yozamiz. Nol siljishda `Intl` shunchaki "GMT" qaytaradi.
 */
export function zoneOffsetLabel(zone: string = getDisplayTimeZone()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date())
    const name = parts.find((p) => p.type === 'timeZoneName')?.value
    if (!name) return 'UTC+00:00'
    const match = /^(?:GMT|UTC)([+-])(\d{1,2})(?::?(\d{2}))?$/.exec(name)
    if (!match) return 'UTC+00:00' // "GMT" yoki "UTC" — siljishsiz
    const sign = match[1] ?? '+'
    const hh = (match[2] ?? '0').padStart(2, '0')
    const mm = match[3] ?? '00'
    return `UTC${sign}${hh}:${mm}`
  } catch {
    return 'UTC+00:00'
  }
}

const dateTimeCache = new Map<string, Intl.DateTimeFormat>()

/**
 * Kesh kaliti ZONANI ham o'z ichiga oladi: aks holda tenant zonasi
 * o'zgargandan keyin ham eski zonadagi formatlagich qaytarilardi.
 */
function dateTimeFormat(locale: string, dateOnly: boolean, zone: string): Intl.DateTimeFormat {
  const key = `${locale}|${String(dateOnly)}|${zone}`
  const cached = dateTimeCache.get(key)
  if (cached) return cached

  const options: Intl.DateTimeFormatOptions = dateOnly
    ? { year: 'numeric', month: '2-digit', day: '2-digit' }
    : {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }

  let fmt: Intl.DateTimeFormat
  try {
    fmt = new Intl.DateTimeFormat(locale, { ...options, timeZone: zone })
  } catch {
    // Zona `setDisplayTimeZone` da tekshirilgan, ya'ni bu yerga kelmasligi
    // kerak. Kelib qolsa — vaqtni umuman ko'rsatmaslikdan ko'ra brauzer
    // zonasida ko'rsatgan yaxshi.
    fmt = new Intl.DateTimeFormat(locale, options)
  }
  dateTimeCache.set(key, fmt)
  return fmt
}

/** ISO UTC → tenant zonasidagi vaqt matni (tanlangan tilga mos) */
export function formatDateTime(iso: string | null | undefined): string {
  return formatDateTimeInZone(iso, getDisplayTimeZone())
}

/** ISO UTC → tenant zonasidagi sana matni */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return dateTimeFormat(currentIntlLocale(), true, getDisplayTimeZone()).format(d)
}

/**
 * ISO UTC → BERILGAN zonadagi vaqt matni.
 * Sozlamalar sahifasida kerak: operator zonani saqlashdan OLDIN uning
 * ta'sirini ko'rishi kerak, ya'ni hali o'rnatilmagan zonada formatlash.
 */
export function formatDateTimeInZone(iso: string | null | undefined, zone: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return dateTimeFormat(currentIntlLocale(), false, zone).format(d)
}

/**
 * Tooltip uchun UCH qator: UTC, tenant zonasi va (farq qilsa) brauzer zonasi.
 *
 * NEGA UCHALASI: raqamga shubha tug'ilganda operator ASL qiymatni ko'ra
 * olishi kerak. Brauzer qatori esa aynan chalkashlik manbaini ochib beradi —
 * "menda soat boshqacha" degan savolga javob shu yerda. Zonalar bir xil
 * bo'lsa qator qo'shilmaydi: takroriy ma'lumot tooltipni shovqinlashtiradi.
 */
export function utcTooltip(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''

  const display = getDisplayTimeZone()
  const browser = browserTimeZone()
  const utcText = d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, '')

  const lines = [
    `${i18n.t('timezone.utc')}: ${utcText}`,
    `${i18n.t('timezone.tenant')} (${display} · ${zoneOffsetLabel(display)}): ${formatDateTimeInZone(iso, display)}`,
  ]
  if (browser !== display) {
    lines.push(
      `${i18n.t('timezone.browser')} (${browser} · ${zoneOffsetLabel(browser)}): ${formatDateTimeInZone(iso, browser)}`,
    )
  }
  return lines.join('\n')
}

/**
 * BRAUZER zonasining siljish belgisi, masalan "UTC+05:00".
 * Ma'nosi ataylab o'zgarmagan: sozlamalar sahifasi tenant zonasi bilan
 * brauzernikini YONMA-YON ko'rsatadi, ya'ni ikkalasi ham kerak.
 */
export function localZoneLabel(): string {
  return zoneOffsetLabel(browserTimeZone())
}

/* =====================================================================
   KUN CHEGARASI — TENANT zonasida, brauzernikida EMAS

   ⚠️ NEGA BU BO'LIM BOR. `dayjs().startOf('day')` va
   `dayjs().format('YYYY-MM-DD')` BRAUZER zonasida ishlaydi. Kobulda
   (UTC+04:30) kun UTC 19:30 da boshlanadi, Toshkentda (UTC+05:00) esa
   19:00 da — ya'ni kuniga YARIM SOAT davomida ikki shahar ikki xil sanada
   turadi. Toshkentdan boshqarilayotgan Kobul tenantida "bugun" panelda va
   serverda BOSHQA kun bo'lardi.

   ⚠️ VA BU JIMGINA NUQSON. Xato chiqmaydi, so'rov 200 qaytaradi, jadval
   to'ladi — faqat SON boshqacha bo'ladi. Backend chegarani tenantning
   `day_timezone` iga qarab hisoblaydi (moliyaviy davr, `usage_record`),
   panel esa brauzerga qarab so'rardi.

   ⚠️ `dayjs` ning `timezone` plagini ATAYLAB olinmadi: u o'zi bilan
   `utc` plaginini va butun IANA jadvalini tortadi, holbuki bu yerda
   kerak bo'lgani `Intl` da allaqachon bor. Bog'liqlik qo'shish o'rniga
   to'rtta sof funksiya yozildi.
   ===================================================================== */

/** `YYYY-MM-DD` — serverdagi `ReportWindow` kutadigan shakl */
const ISO_DAY_SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/

/** `YYYY-MM-DD` → `[yil, oy, kun]`; shakli boshqa bo'lsa `null` */
function dayParts(day: string): [number, number, number] | null {
  const m = ISO_DAY_SHAPE.exec(day)
  if (!m) return null
  return [Number(m[1]), Number(m[2]), Number(m[3])]
}

/** `Date` ning UTC qismlaridan `YYYY-MM-DD` */
function isoDayOfUtc(d: Date): string {
  const p = (n: number, width = 2) => String(n).padStart(width, '0')
  return `${p(d.getUTCFullYear(), 4)}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`
}

/**
 * Zonaning AYNAN SHU DAMDAGI siljishi, daqiqada (Kobul uchun 270).
 *
 * ⚠️ `Date.getTimezoneOffset()` YARAMAYDI — u faqat brauzer zonasini
 * biladi. Bu yerda esa boshqa zonaning siljishi kerak va uni olishning
 * yagona yo'li — o'sha zonada formatlab, natijani UTC deb qayta o'qish.
 *
 * ⚠️ SILJISH DAMGA BOG'LIQ: yozgi vaqtga o'tadigan zonalarda u yilning
 * ikki qismida ikki xil. Shuning uchun `at` majburiy parametr —
 * "hozirgi" siljishni boshqa kunga qo'llash mart oyida bir soatlik
 * xatoga olib kelardi.
 */
function zoneOffsetMinutes(zone: string, at: Date): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(at)
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0')
    // ⚠️ `hour12: false` ba'zi muhitlarda yarim tunni "24" deb beradi
    const wallUtc = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour') % 24,
      get('minute'),
      get('second'),
    )
    // `at` dagi millisekundlar `wallUtc` da yo'q — solishtirish sekundda
    return Math.round((wallUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000)
  } catch {
    return 0
  }
}

/**
 * TENANT zonasidagi bugungi kalendar kuni, `YYYY-MM-DD`.
 *
 * ⚠️ `dayjs().format('YYYY-MM-DD')` NING O'RNIGA. Farqi kuniga yarim
 * soat ko'rinadi, lekin aynan o'sha yarim soatda hisobot bir kunga
 * surilib ketardi.
 */
export function todayInTenantZone(zone: string = getDisplayTimeZone()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date())
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
    return `${get('year')}-${get('month')}-${get('day')}`
  } catch {
    return isoDayOfUtc(new Date())
  }
}

/**
 * Kalendar kunini N kun surish: `shiftDay('2026-03-01', -1)` → `2026-02-28`.
 *
 * ⚠️ Sof KALENDAR arifmetikasi, zona bu yerga umuman kirmaydi: "kecha"
 * har qanday zonada ham bir kun oldingi sana. Zona faqat o'sha kunning
 * QAYSI ONDA boshlanishini aniqlaydi ({@link startOfDayIso}).
 */
export function shiftDay(day: string, days: number): string {
  const parts = dayParts(day)
  if (!parts) return day
  const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]))
  d.setUTCDate(d.getUTCDate() + days)
  return isoDayOfUtc(d)
}

/**
 * `YYYY-MM-DD` (TENANT zonasidagi kun) → o'sha kun BOSHLANGAN on, UTC ISO.
 * Kobulda `2026-08-05` → `2026-08-04T19:30:00.000Z`.
 *
 * ⚠️ IKKI QADAM: siljish qaysi ondaligiga bog'liq, biz esa hali o'sha onni
 * bilmaymiz. Birinchi taxmin devor-soati bo'yicha olinadi, ikkinchisi uni
 * to'g'rilaydi — yozgi vaqt chegarasida ham bir soat adashmasin.
 *
 * ⚠️ Shakli buzuq qiymat O'ZGARISHSIZ qaytadi: bu yerda "taxminiy" sana
 * o'ylab topish server 400 beradigan holatdan YOMONROQ — u jimgina
 * noto'g'ri hisobot bo'lardi.
 */
export function startOfDayIso(day: string, zone: string = getDisplayTimeZone()): string {
  const parts = dayParts(day)
  if (!parts) return day
  const wall = Date.UTC(parts[0], parts[1] - 1, parts[2], 0, 0, 0)
  const first = wall - zoneOffsetMinutes(zone, new Date(wall)) * 60000
  const exact = wall - zoneOffsetMinutes(zone, new Date(first)) * 60000
  return new Date(exact).toISOString()
}

/**
 * `YYYY-MM-DD` → o'sha kunning OXIRGI millisekundi, UTC ISO.
 * Kobulda `2026-08-05` → `2026-08-05T19:29:59.999Z`.
 *
 * ⚠️ `.999` — antd `endOf('day')` bilan AYNAN bir xil semantika, ya'ni
 * `to` chegarasi ICHIGA KIRADI. Buni `T00:00` ga aylantirish tanlangan
 * oxirgi kunni butunlay tushirib qoldirardi.
 */
export function endOfDayIso(day: string, zone: string = getDisplayTimeZone()): string {
  const next = Date.parse(startOfDayIso(shiftDay(day, 1), zone))
  if (Number.isNaN(next)) return day
  return new Date(next - 1).toISOString()
}

// =====================================================================
// ⚠️ ZONA BO'LIMINING OXIRI — `tests/timezone.mjs` shu belgigacha o'qiydi
// (`REGION_END`). Yangi zona funksiyasini shu belgidan YUQORIGA qo'ying,
// aks holda u sinovga umuman tushmaydi.
//
// ⚠️ Belgi ATAYLAB satrli izoh (`//`), blokli (`/* */`) emas: sinov
// matnni shu belgigacha KESADI, blokli izohda esa kesilgan bo'lak
// yopilmagan `/*` bilan tugab, "Invalid or unexpected token" berardi.
// =====================================================================
